import { ChangeDetectionStrategy, Component, Input } from "@angular/core";

import { ChipColor } from "../../shared/chip-types";
import { CHIP_DESIGNS, DEFAULT_CHIP_DESIGN_ID } from "../../shared/chip-colors";


export type BvChipSize = "sm" | "md" | "lg" | "xl";
type RenderMode = "empty" | "solo" | "two-tone";

/**
 * Poker-chip visual primitive. Renders an inline SVG whose two regions
 * (body, edge spots) recolor via `--chip-primary` / `--chip-secondary`
 * CSS custom properties bound from the `color` input.
 *
 *   <bv-chip [denom]="25" [color]="{ p: '#27ae60', s: '#f5f5f5' }"></bv-chip>
 *   <bv-chip [denom]="100" [color]="{ p: '#1c1c1c' }" size="lg"></bv-chip>  <!-- single-tone -->
 *   <bv-chip [denom]="500"></bv-chip>                                      <!-- empty / outline -->
 */
@Component({
    selector: "bv-chip",
    templateUrl: "./chip.component.html",
    styleUrls: ["./chip.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "bv-chip",
        "[class.bv-chip--sm]": 'size === "sm"',
        "[class.bv-chip--md]": 'size === "md"',
        "[class.bv-chip--lg]": 'size === "lg"',
        "[class.bv-chip--xl]": 'size === "xl"',
        "[class.bv-chip--empty]": 'renderMode === "empty"',
        "[style.--chip-primary]": "primaryCss",
        "[style.--chip-secondary]": "secondaryCss",
    },
    imports: []
})
export class BvChipComponent {
  @Input() denom?: number | null;
  @Input() color?: ChipColor | null;
  @Input() design?: string | null;
  @Input() size: BvChipSize = "md";
  @Input() showLabel = true;

  get renderMode(): RenderMode {
    if (!this.color?.p) return "empty";
    if (!this.color.s) return "solo";
    return "two-tone";
  }

  get primaryCss(): string | null {
    return this.color?.p || null;
  }

  get secondaryCss(): string | null {
    return this.color?.s || this.color?.p || null;
  }

  get designId(): string {
    return this.design || DEFAULT_CHIP_DESIGN_ID;
  }

  get isClassic(): boolean {
    return this.designId === "classic";
  }

  get isStriped(): boolean {
    return this.designId === "striped";
  }

  get isBlades(): boolean {
    return this.designId === "blades";
  }

  get isDice(): boolean {
    return this.designId === "dice";
  }

  get isOrnate(): boolean {
    return this.designId === "ornate";
  }

  get label(): string {
    const n = this.denom;
    if (n == null || isNaN(n)) return "";
    if (n < 1000 || (n < 10000 && n % 1000 !== 0)) return String(n);
    if (n < 1000000) {
      const v = n / 1000;
      const r = v < 10 ? Math.round(v * 100) / 100 : Math.round(v * 10) / 10;
      return r + "K";
    }
    const v = n / 1000000;
    const r = v < 10 ? Math.round(v * 100) / 100 : Math.round(v * 10) / 10;
    return r + "M";
  }

  // Exposed for the design-picker / style-guide.
  static readonly DESIGNS = CHIP_DESIGNS;
}
